import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { Encounter, Questionnaire, QuestionnaireResponse } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Drawer, useDrawersStack } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";

import { Loading } from "@/components/Loading";
import { TimerProvider } from "@/components/Timer/TimerContext";
import { EncounterContext } from "@/contexts/encounter";
import { EncounterLaborAmendContent } from "@/encounters/components/EncounterLabor/EncounterLaborAmend/EncounterLaborAmendContent";
import { useLaborAmendInitialData } from "@/encounters/components/EncounterLabor/hooks/use-labor-amend-initial-data";
import { useResetLaborAmendOnContextChange } from "@/encounters/components/EncounterLabor/hooks/use-reset-labor-amend-on-context-change";
import { useBirthObservations } from "@/encounters/components/EncounterLabor/hooks/useBirthObservations";
import { saveLaborAmendEncounterQuestionnaireResponse } from "@/encounters/components/EncounterLabor/utils/labor-amend-encounter-questionnaire-save";
import { stripLaborAmendPreviewStages } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview-labor-stages";
import { isLaborAmendFlowsheetEditingEnabled } from "@/encounters/components/EncounterLabor/utils/labor-amend-sign-and-lock-ready";
import { FileProvider, FilesDrawer } from "@/encounters/components/shared/Files";
import { DefaultEncounterDrawerFormsStack } from "@/encounters/constants/encounter-drawer-forms-stack";
import { BirthMilestonesProvider } from "@/encounters/contexts/BirthMilestonesProvider";
import {
  EncounterChildSaveProvider,
  useEncounterChildSaveCoordinator,
} from "@/encounters/contexts/EncounterChildSaveContext";
import { LaborAmendFlowsheetPersistProvider } from "@/encounters/contexts/LaborAmendFlowsheetPersistContext";
import useEncounterChargeCapture from "@/encounters/hooks/useEncounterChargeCapture";
import { fetchEncounterQuestionnaire } from "@/encounters/services/questionnaire";
import { fetchEncounterQuestionnaireResponse } from "@/encounters/services/questionnaire-response";
import {
  EncounterAmendmentFormSchema,
  EncounterAmendmentFormSchemaRequired,
  EncounterAmendmentFormType,
  getEncounterInitialValues,
} from "@/encounters/zod-schemas/encounter-answers";
import { DEFAULT_ENCOUNTER_SERVICE_DATE_DETAILS } from "@/encounters/zod-schemas/encounter-service-date-details";
import { Status } from "@/encounters/zod-schemas/encounters";
import { Systems } from "@/fhir/enums/systems";
import { RootState } from "@/store";
import { getCodeableConceptDisplay } from "@/utils/medplum";

const EncounterLaborAmend = () => {
  const { encounterId, clientId } = useParams<{ encounterId: string; clientId: string }>();
  const navigate = useNavigate();
  const medplum = useMedplum();
  const dispatch = useDispatch();
  const laborStageEntities = useSelector((state: RootState) => state.laborStages.entities);
  const { clearOptimisticBirth } = useBirthObservations(encounterId, clientId);
  const childSaveCoordinator = useEncounterChildSaveCoordinator();
  useEncounterChargeCapture({ coordinator: childSaveCoordinator, clientId, encounterId });
  const { laborStagesLoadStatus, birthObservationsLoadStatus } = useLaborAmendInitialData({
    clientId,
    encounterId,
    medplum,
  });

  useResetLaborAmendOnContextChange({ encounterId, clientId });

  const stack = useDrawersStack([DefaultEncounterDrawerFormsStack.FILES]);
  const stripPreviewLaborStages = useCallback(() => {
    stripLaborAmendPreviewStages(dispatch, laborStageEntities);
  }, [dispatch, laborStageEntities]);

  const handleDiscardAmendWork = useCallback(() => {
    clearOptimisticBirth();
    stripPreviewLaborStages();
  }, [clearOptimisticBirth, stripPreviewLaborStages]);

  const [encounter, setEncounter] = useState<Encounter | undefined>(undefined);
  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [questionnaireResponse, setQuestionnaireResponse] = useState<
    QuestionnaireResponse | undefined
  >(undefined);
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | undefined>();

  const form = useForm<EncounterAmendmentFormType>({
    initialValues: {
      encounterNote: "",
      encounterClientSummary: "",
      encounterVitalSigns: {},
      encounterFlowSheet: {},
      encounterDetails: {
        encounterTemplate: { system: "", code: "", display: "" },
        encounterType: { system: "", code: "", display: "" },
        encounterLocation: { system: Systems.LOINC, code: "", display: "" },
        ...DEFAULT_ENCOUNTER_SERVICE_DATE_DETAILS,
      },
      encounterAmendmentInfo: {
        encounterAmendmentInfoReason: "",
        encounterAmendmentInfoSpecify: "",
        encounterAmendmentInfoNotes: "",
        encounterAmendmentInfoNotifyClient: false,
      },
      encounterClientDiagnoses: [],
    },
    validate: zodResolver(EncounterAmendmentFormSchemaRequired),
  });

  const setFormValues = form.setValues;

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!encounterId || !clientId) return;

      const encounter = await medplum.readResource("Encounter", encounterId);

      if (!encounter) return;

      const encounterQuestionnaire = await fetchEncounterQuestionnaire(medplum, encounter);

      if (encounterQuestionnaire) {
        setQuestionnaire(encounterQuestionnaire);

        const encounterQuestionnaireResponse = await fetchEncounterQuestionnaireResponse({
          encounterId,
          questionnaireId: encounterQuestionnaire.id!,
          clientId,
          medplum,
        });

        setQuestionnaireResponse(encounterQuestionnaireResponse);
        if (encounterQuestionnaireResponse) {
          setFormValues(getEncounterInitialValues(encounterQuestionnaireResponse, encounter));
        }
      }

      setEncounter(encounter);
      setIsFetchingInitialData(false);
    };
    void fetchInitialData();
  }, [clientId, encounterId, medplum, setFormValues]);

  const handleEncounterQuestionnaireSave = async (
    values: EncounterAmendmentFormType,
  ): Promise<QuestionnaireResponse> => {
    const parsedValues = EncounterAmendmentFormSchema.parse(values);

    if (!questionnaire || !questionnaireResponse) {
      throw new Error(
        "Encounter questionnaire data is not loaded. Refresh the page and try again.",
      );
    }

    const savedQuestionnaireResponse = await saveLaborAmendEncounterQuestionnaireResponse({
      medplum,
      questionnaireResponse,
      parsedValues,
    });
    setQuestionnaireResponse(savedQuestionnaireResponse);

    return savedQuestionnaireResponse;
  };

  const template = getCodeableConceptDisplay(encounter?.serviceType)?.toLowerCase() || "";

  const isEncounterActive =
    encounter?.status !== Status.Finished && encounter?.status !== Status.Cancelled;

  if ((!isFetchingInitialData && !encounterId && !encounter) || (encounter && isEncounterActive)) {
    navigate(`/clients/${clientId}/encounters/${encounterId}`, { preventScrollReset: true });
    return;
  }

  if (isFetchingInitialData || !encounter || !questionnaire || !questionnaireResponse) {
    return <Loading />;
  }

  const isEncounterDataReady = Boolean(questionnaire && questionnaireResponse);

  return (
    <FileProvider medplum={medplum} clientId={clientId!}>
      <BirthMilestonesProvider encounter={encounter}>
        <TimerProvider>
          <LaborAmendFlowsheetPersistProvider
            encounterId={encounterId!}
            onDiscardAmendWork={handleDiscardAmendWork}
            laborStagesLoadStatus={laborStagesLoadStatus}
            birthObservationsLoadStatus={birthObservationsLoadStatus}
            initialEncounterQuestionnaireParentVersionId={questionnaireResponse.meta?.versionId}
            isFlowsheetEditingEnabled={isLaborAmendFlowsheetEditingEnabled({
              laborStagesLoadStatus,
              birthObservationsLoadStatus,
            })}
          >
            <EncounterContext.Provider value={{ encounter, setEncounter }}>
              <EncounterChildSaveProvider coordinator={childSaveCoordinator}>
                <EncounterLaborAmendContent
                  form={form}
                  stack={stack}
                  medplum={medplum}
                  template={template}
                  clientId={clientId!}
                  encounter={encounter!}
                  encounterId={encounterId!}
                  isEncounterActive={isEncounterActive}
                  isEncounterDataReady={isEncounterDataReady}
                  laborStagesLoadStatus={laborStagesLoadStatus}
                  questionnaireResponse={questionnaireResponse}
                  onSignAndLock={handleEncounterQuestionnaireSave}
                  onStripPreviewLaborStages={stripPreviewLaborStages}
                  birthObservationsLoadStatus={birthObservationsLoadStatus}
                />
              </EncounterChildSaveProvider>
            </EncounterContext.Provider>
          </LaborAmendFlowsheetPersistProvider>
          <Drawer.Stack>
            <FilesDrawer
              register={stack.register}
              encounterId={encounterId!}
              filesDrawerId={DefaultEncounterDrawerFormsStack.FILES}
            />
          </Drawer.Stack>
        </TimerProvider>
      </BirthMilestonesProvider>
    </FileProvider>
  );
};

export default EncounterLaborAmend;
